const checkForbiddenPatterns = (pwd, lowerCasePwd) => {
  const forbiddenPatterns = [
    /abc|bcd|cde|def|efg|fgh|ghi|hij|ijk|jkl|klm|lmn|mno|nop|opq|pqr|rst|stu|tuv|uvw|vwx|wxy|xyz/i,
    /123|234|345|456|567|678|789|987|876|765|654|543|432|321/i,
    /(\w)\1\1/
  ];

  const forbiddenMessages = [
    'No puede contener letras consecutivas (abc, def, etc).',
    'No puede contener números consecutivos (123, 456, etc).',
    'No puede contener caracteres repetidos consecutivos.'
  ];

  for (let i = 0; i < forbiddenPatterns.length; i++) {
    if (forbiddenPatterns[i].test(pwd)) {
      return { valid: false, error: forbiddenMessages[i] };
    }
  }

  if (
    lowerCasePwd.includes('password') ||
    lowerCasePwd.includes('contraseña')
  ) {
    return {
      valid: false,
      error: 'No puede contener "password" o "contraseña".'
    };
  }

  return { valid: true };
};

const checkPersonalInfo = (
  lowerCasePwd,
  username,
  clientType,
  businessName
) => {
  if (username) {
    const names = username
      .toLowerCase()
      .split(' ')
      .filter(name => name.length > 2);
    if (names.some(name => lowerCasePwd.includes(name))) {
      return {
        valid: false,
        error: 'La contraseña no puede contener tu nombre o apellido'
      };
    }
  }

  if (clientType === 'legal-person' && businessName) {
    if (lowerCasePwd.includes(businessName.toLowerCase())) {
      return {
        valid: false,
        error: 'La contraseña no puede contener la razon social.'
      };
    }
  }

  return { valid: true };
};

const checkLength = (pwd, score) => {
  const errors = [];
  let updatedScore = score;

  if (pwd.length < 8) {
    errors.push('Mínimo 8 caracteres.');
  } else {
    updatedScore += 5;
    if (pwd.length > 8) {
      updatedScore += Math.min(pwd.length - 8, 8);
    }
    if (pwd.length >= 16) {
      updatedScore += 6;
    }
  }

  return { score: updatedScore, errors };
};

const checkCharacterClasses = (pwd, score) => {
  const errors = [];
  let updatedScore = score;

  if (/[A-Z]/.test(pwd)) {
    updatedScore += 4;
    const upperCaseCount = (pwd.match(/[A-Z]/g) || []).length;
    if (upperCaseCount > 1) {
      updatedScore += Math.min(upperCaseCount - 1, 2);
    }
  } else {
    errors.push('Debe contener al menos una letra mayúscula.');
  }

  if (/[a-z]/.test(pwd)) {
    updatedScore += 4;
    const lowerCaseCount = (pwd.match(/[a-z]/g) || []).length;
    if (lowerCaseCount > 2) {
      updatedScore += Math.min(lowerCaseCount - 2, 2);
    }
  } else {
    errors.push('Debe contener al menos una letra minúscula.');
  }

  if (/\d/.test(pwd)) {
    updatedScore += 4;
    const digitCount = (pwd.match(/\d/g) || []).length;
    if (digitCount > 1) {
      updatedScore += Math.min(digitCount - 1, 3);
    }
  } else {
    errors.push('Debe contener al menos un número.');
  }

  if (/[^A-Za-z0-9]/.test(pwd)) {
    updatedScore += 8;
    const specialCount = (pwd.match(/[^A-Za-z0-9]/g) || []).length;
    if (specialCount > 1) {
      updatedScore += Math.min(specialCount - 1, 4) * 2;
    }
  } else {
    errors.push('Debe contener al menos un carácter especial.');
  }

  return { score: updatedScore, errors };
};

export const validatePassword = (
  pwd,
  clientType,
  username,
  businessName,
  currentPassword
) => {
  const errors = [];
  let score = 0;

  if (!pwd) {
    return { strength: 0, errors: [] };
  }

  const lowerCasePwd = pwd.toLowerCase();

  if (currentPassword && pwd === currentPassword) {
    return {
      strength: 0,
      errors: ['La nueva contraseña debe ser diferente a la actual']
    };
  }

  const patternCheck = checkForbiddenPatterns(pwd, lowerCasePwd);
  if (!patternCheck.valid) {
    return { strength: 0, errors: [patternCheck.error] };
  }

  const personalCheck = checkPersonalInfo(
    lowerCasePwd,
    username,
    clientType,
    businessName
  );
  if (!personalCheck.valid) {
    return { strength: 0, errors: [personalCheck.error] };
  }

  const lengthCheck = checkLength(pwd, score);
  score = lengthCheck.score;
  errors.push(...lengthCheck.errors);

  const charCheck = checkCharacterClasses(pwd, score);
  score = charCheck.score;
  errors.push(...charCheck.errors);

  if (errors.length === 0) {
    score = Math.max(score, 20);
    const hasComplexPattern = /(?:[A-Z].*[0-9]|[0-9].*[A-Z]).*[^A-Za-z0-9]|[^A-Za-z0-9].*(?:[A-Z].*[0-9]|[0-9].*[A-Z])/.test(
      pwd
    );
    if (hasComplexPattern) {
      score += 5;
    }
  }

  return { strength: score, errors };
};

export const STRENGTH_THRESHOLDS = {
  WEAK: 0,
  MODERATE: 20,
  STRONG: 30,
  VERY_STRONG: 40
};

export const getPasswordStrengthLevel = (score, errors = []) => {
  if (errors.length > 0 || score === 0) {
    return {
      level: 1,
      text: 'Débil',
      isStrong: false
    };
  }

  if (score >= STRENGTH_THRESHOLDS.VERY_STRONG) {
    return { level: 4, text: 'Muy fuerte', isStrong: true };
  }

  if (score >= STRENGTH_THRESHOLDS.STRONG) {
    return { level: 3, text: 'Fuerte', isStrong: true };
  }

  return { level: 2, text: 'Moderada', isStrong: true };
};

export const strengthColors = {
  0: '#808080',
  1: '#ff0000',
  2: '#ffa500',
  3: '#90ee90',
  4: '#008000'
};

export const STRENGTH_LABELS = [
  'Ingrese contraseña',
  'Débil',
  'Moderada',
  'Fuerte',
  'Muy fuerte'
];
